import {
  ActionIcon,
  Menu,
  Text,
  Checkbox,
  Flex,
  useMantineTheme,
} from '@mantine/core';
import { FunnelIcon, CaretDownIcon } from '@phosphor-icons/react';

import { SOURCE_TYPE_ICONS } from 'modules/shared/libs/chat/utils/source-url-parser';
import TRACKING_IDS from 'tracking-ids';

import type { Source } from 'modules/shared/api/sources.api.types';

const filterSelectableSources = (sources: Source[]): Source[] => {
  return sources.filter(
    (source) => !source.is_required && source.type !== 'UNKNOWN',
  );
};

type SourceFilterButtonProps = {
  sources: Source[];
  disabledSources: string[];
  onDisabledSourcesChange: (disabledSources: string[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
};

export function SourceFilterButton({
  sources,
  disabledSources,
  onDisabledSourcesChange,
  disabled,
  isLoading,
  size = 'sm',
}: SourceFilterButtonProps) {
  const { primaryColor } = useMantineTheme();
  const selectableSources = filterSelectableSources(sources);
  const selectableTypes = new Set(selectableSources.map((s) => s.type));

  // Only count disabled entries that map to a row the user can actually
  // see and toggle. Otherwise a schedule originally saved against a
  // different source surface (e.g., research-mode or legacy) can land in
  // the answer-mode picker carrying disabled types that aren't selectable
  // here — `disabledSources.length` over-counts, `enabledCount` goes 0 or
  // negative, and the "keep at least one enabled" guard silently fails.
  const disabledSelectableCount = disabledSources.filter((t) =>
    selectableTypes.has(t),
  ).length;
  const enabledCount = selectableSources.length - disabledSelectableCount;
  const hasDisabledSources = disabledSelectableCount > 0;
  const isLastEnabledSource = enabledCount === 1;

  const handleToggleSource = (sourceType: string) => {
    const isCurrentlyEnabled = !disabledSources.includes(sourceType);

    if (isCurrentlyEnabled && isLastEnabledSource) {
      return;
    }

    if (disabledSources.includes(sourceType)) {
      onDisabledSourcesChange(disabledSources.filter((s) => s !== sourceType));
    } else {
      onDisabledSourcesChange([...disabledSources, sourceType]);
    }
  };

  return (
    <Menu
      position="bottom-start"
      withinPortal
      floatingStrategy="fixed"
      width={240}
      closeOnItemClick={false}
      middlewares={{
        flip: true,
        shift: { padding: 8, mainAxis: true, crossAxis: true },
      }}
    >
      <Menu.Target>
        <ActionIcon
          variant="light"
          color={hasDisabledSources ? primaryColor : 'gray'}
          size={size}
          w={40}
          disabled={disabled || isLoading}
          data-tracking-id={TRACKING_IDS.hatch.chat.sources.filter}
        >
          <Flex align="center" gap={2}>
            <FunnelIcon
              size={16}
              weight={hasDisabledSources ? 'fill' : 'regular'}
            />
            <CaretDownIcon size={12} />
          </Flex>
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown
        style={{ maxHeight: 'calc(100vh - 16px)', overflowY: 'auto' }}
      >
        <Menu.Label>Filter sources</Menu.Label>
        {selectableSources.map((source) => {
          const Icon = SOURCE_TYPE_ICONS[source.type];
          const isEnabled = !disabledSources.includes(source.type);
          const isDisabledToggle = isEnabled && isLastEnabledSource;

          return (
            <Menu.Item
              key={source.type}
              leftSection={<Icon size={16} />}
              onClick={() => handleToggleSource(source.type)}
              disabled={isDisabledToggle}
            >
              <Checkbox
                checked={isEnabled}
                label={
                  <Text size="sm" component="span">
                    {source.friendly_name}
                  </Text>
                }
                readOnly
                styles={{
                  root: { pointerEvents: 'none' },
                  input: { cursor: 'pointer' },
                }}
              />
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}

export default SourceFilterButton;
